import { AlertType, PriceList, Season, TimeSlot } from '@types';
import {
  AlertModal,
  Card,
  EmptyState,
  LoadingSpinner,
  PageHeader,
  SectionHeader,
} from '_atoms';
import { AdminService } from '_services';
import { HelperMethods } from '_utils';
import React, { useCallback, useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import {
  MdCelebration,
  MdEvent,
  MdLink,
  MdPeople,
  MdPriceCheck,
  MdSave,
  MdSchedule,
  MdToggleOff,
  MdToggleOn,
} from 'react-icons/md';

const SkatingSessions: React.FC = () => {
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [updatingSlotId, setUpdatingSlotId] = useState<string | null>(null);
  const [priceList, setPriceList] = useState<PriceList[]>([]);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // Load current season details on page load
  useEffect(() => {
    const loadCurrentSeason = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await AdminService.getCurrentSeasonDetails()) as unknown as Season;

        if (
          responseData &&
          (responseData.startDate ||
            responseData.endDate ||
            responseData.sessionTimes.length > 0)
        ) {
          setSettings(responseData);
        } else {
          showAlert(
            'info',
            'Info',
            'There are currently no active season settings.',
          );
        }
      } catch (error) {
        console.error('Error loading current season:', error);
        showAlert('error', 'Error', 'Failed to load current season settings.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadCurrentSeason();
  }, []);

  // Load price list on page load
  useEffect(() => {
    const loadPriceList = async () => {
      try {
        const responseData =
          (await AdminService.getCurrentPriceList()) as unknown as PriceList[];
        setPriceList(responseData || []);
      } catch (error) {
        console.error('Error loading price list:', error);
      }
    };

    loadPriceList();
  }, []);

  const showAlert = (type: AlertType, title: string, message: string) => {
    setAlertModal({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    if (date) {
      loadTimeSlotsForDate(date);
    } else {
      setTimeSlots([]);
    }
  };

  const loadTimeSlotsForDate = useCallback(async (date: string) => {
    setIsLoadingTimeSlots(true);
    try {
      const responseData = (await AdminService.getTimeSlotsForDate(
        date,
      )) as unknown as TimeSlot[];
      setTimeSlots(responseData || []);
    } catch (error) {
      console.error('Error loading time slots:', error);
      showAlert(
        'error',
        'Error',
        'Failed to load skating sessions for the selected date.',
      );
      setTimeSlots([]);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  }, []);

  const handleTimeSlotChange = (
    id: string,
    field: keyof TimeSlot,
    value: string | number | boolean,
  ) => {
    setTimeSlots((prev: TimeSlot[]) =>
      prev.map((slot: TimeSlot) =>
        slot.id === id ? { ...slot, [field]: value } : slot,
      ),
    );
  };

  const handleUpdateTimeSlot = useCallback(
    async (timeSlot: TimeSlot) => {
      setIsLoadingUpdate(true);
      setUpdatingSlotId(timeSlot.id);

      try {
        await AdminService.updateTimeSlot(timeSlot);
        showAlert(
          'success',
          'Success',
          `Session ${timeSlot.timeSlot} updated successfully!`,
        );

        // Refresh the time slots list for the current selected date
        if (selectedDate) {
          await loadTimeSlotsForDate(selectedDate);
        }
      } catch (error) {
        console.error('Error updating time slot:', error);
        showAlert(
          'error',
          'Error',
          'Error updating session. Please try again.',
        );
      } finally {
        setIsLoadingUpdate(false);
        setUpdatingSlotId(null);
      }
    },
    [selectedDate, loadTimeSlotsForDate],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdSchedule}
        title="Skating Sessions"
        description="Manage skating sessions for specific dates. Select a date to view and update session details."
      />

      <div className="flex flex-col md:flex-row gap-4">
        {/* Date Selection */}
        <div>
          {settings && (
            <Card>
              <DayPicker
                mode="single"
                selected={new Date(selectedDate)}
                onSelect={e => handleDateChange(e?.toISOString() || '')}
                modifiers={{
                  disabled:
                    settings.startDate && settings.endDate
                      ? [
                          { before: new Date(settings.startDate) },
                          { after: new Date(settings.endDate) },
                        ]
                      : [],
                }}
                footer={
                  <div className="text-sm text-gray-500 mt-2">
                    {isLoadingInitial && (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner />
                      </div>
                    )}
                    {settings.startDate && settings.endDate && (
                      <div>
                        Season runs from{' '}
                        {new Date(settings.startDate).toLocaleDateString()} to{' '}
                        {new Date(settings.endDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                }
              />
            </Card>
          )}
          {!settings.startDate && !isLoadingInitial && (
            <EmptyState
              title="No Season Settings"
              description="Please configure season settings before managing skating sessions."
              icon={MdEvent}
            />
          )}
        </div>

        <div className="flex-1">
          <div>
            {/* Add an empty state */}
            {!selectedDate && (
              <EmptyState
                title="Select a Date"
                description="Choose a date from the calendar to view and manage skating sessions."
                icon={MdEvent}
              />
            )}
          </div>
          {/* Time Slots */}
          {selectedDate && (
            <Card className="flex flex-col">
              <SectionHeader
                title={`Sessions for ${new Date(selectedDate).toLocaleDateString()}`}
                icon={MdSchedule}
              />

              {isLoadingTimeSlots ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : timeSlots.length === 0 ? (
                <EmptyState
                  title="No Skating Sessions Found"
                  description={`No skating sessions found for ${new Date(selectedDate).toLocaleDateString()}.`}
                  icon={MdSchedule}
                />
              ) : (
                <div className="space-y-4">
                  {timeSlots.map((timeSlot: TimeSlot, index: number) => (
                    <div
                      key={timeSlot.id}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-900">
                          StartTime:{' '}
                          {HelperMethods.formatTime12Hour(timeSlot.timeSlot)}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Status:</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedTimeSlot = {
                                ...timeSlot,
                                isActive: !timeSlot.isActive,
                              };
                              handleUpdateTimeSlot(updatedTimeSlot);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                              timeSlot.isActive ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                                timeSlot.isActive
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <MdPeople className="mr-1 text-gray-500" />
                            Max Capacity
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="200"
                            value={timeSlot.maxCapacity}
                            onChange={e =>
                              handleTimeSlotChange(
                                timeSlot.id,
                                'maxCapacity',
                                parseInt(e.target.value) || 0,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <MdCelebration className="mr-1 text-gray-500" />
                            Parties Limit
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="200"
                            value={timeSlot.partiesLimit}
                            onChange={e =>
                              handleTimeSlotChange(
                                timeSlot.id,
                                'partiesLimit',
                                parseInt(e.target.value) || 0,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <MdPriceCheck className="mr-1 text-gray-500" />
                            Custom Adult Price Key
                          </label>
                          <select
                            value={
                              timeSlot.customAdultPriceKey
                                ? String(timeSlot.customAdultPriceKey)
                                : ''
                            }
                            onChange={e =>
                              handleTimeSlotChange(
                                timeSlot.id,
                                'customAdultPriceKey',
                                e.target.value || '',
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">None (Use default price)</option>
                            {priceList
                              .filter(item => item.isActive && item.isCustom)
                              .map(item => (
                                <option key={item.id} value={item.type}>
                                  {item.category} - {item.type} - $
                                  {item.price.toFixed(2)}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <MdPriceCheck className="mr-1 text-gray-500" />
                            Custom Child Price Key
                          </label>
                          <select
                            value={
                              timeSlot.customChildPriceKey
                                ? String(timeSlot.customChildPriceKey)
                                : ''
                            }
                            onChange={e =>
                              handleTimeSlotChange(
                                timeSlot.id,
                                'customChildPriceKey',
                                e.target.value || '',
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">None (Use default price)</option>
                            {priceList
                              .filter(item => item.isActive && item.isCustom)
                              .map(item => (
                                <option key={item.id} value={item.type}>
                                  {item.category} - {item.type} - $
                                  {item.price.toFixed(2)}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <MdLink className="mr-1 text-gray-500" />
                            Link Only
                          </label>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                              Enabled:
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                handleTimeSlotChange(
                                  timeSlot.id,
                                  'isLinkOnly',
                                  !timeSlot.isLinkOnly,
                                );
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                timeSlot.isLinkOnly
                                  ? 'bg-blue-600'
                                  : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                                  timeSlot.isLinkOnly
                                    ? 'translate-x-6'
                                    : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {timeSlot.isLinkOnly && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <MdLink className="mr-1 text-gray-500" />
                              Link Only URL Code *
                            </label>
                            <input
                              type="text"
                              value={timeSlot.linkOnlyUrlCode || ''}
                              onChange={e =>
                                handleTimeSlotChange(
                                  timeSlot.id,
                                  'linkOnlyUrlCode',
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                timeSlot.isLinkOnly &&
                                !timeSlot.linkOnlyUrlCode?.trim()
                                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter link only URL code"
                              required
                            />
                            {timeSlot.isLinkOnly &&
                              !timeSlot.linkOnlyUrlCode?.trim() && (
                                <p className="mt-1 text-xs text-red-600">
                                  Link Only URL Code is required when Link Only
                                  is enabled
                                </p>
                              )}
                          </div>
                        )}

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => handleUpdateTimeSlot(timeSlot)}
                            disabled={
                              (isLoadingUpdate &&
                                updatingSlotId === timeSlot.id) ||
                              (timeSlot.isLinkOnly &&
                                !timeSlot.linkOnlyUrlCode?.trim())
                            }
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            {isLoadingUpdate &&
                            updatingSlotId === timeSlot.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Updating...
                              </>
                            ) : (
                              <>
                                <MdSave className="mr-2" />
                                Update
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Status:</span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              timeSlot.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {timeSlot.isActive ? (
                              <>
                                <MdToggleOn className="mr-1" />
                                Active
                              </>
                            ) : (
                              <>
                                <MdToggleOff className="mr-1" />
                                Inactive
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default SkatingSessions;
